// =====================================
// 📁 src/application/stores/auth.store.ts
// =====================================
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getCompanyIdFromToken, getExpirationDateFromToken, isTokenValid } from '../../api/authHelper';
import { getClientToken as getClientTokenUseCase } from '../../application/usecases/getClientToken';
import { login as loginUseCase } from '../../application/usecases/login';
import { refreshTokenUseCase } from '../../application/usecases/refreshToken';
import { sageLogin } from '../../application/usecases/sageLogin';
import i18n from '../../i18n';
import { log } from '../../utils/logger';
import { getCompanyInfoV2 } from '../usecases/getCompanyInfoV2';
import { useCompanyStore } from './company.store';
let logoutTimeout = null;
const setupTokenAutoRefresh = (token, refreshFn) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const expirationTime = getExpirationDateFromToken(token); // en ms
        const now = Date.now();
        const refreshThreshold = 60 * 1000; // 1 minuto antes
        const delay = expirationTime - now - refreshThreshold;
        if (logoutTimeout)
            clearTimeout(logoutTimeout);
        if (isNaN(delay) || delay <= 0) {
            log.warn('setupTokenAutoRefresh: El token ya está expirado o el delay es inválido.');
            refreshFn(); // refrescar de inmediato
            return;
        }
        log.info(`setupTokenAutoRefresh: programando refresh en ${(delay / 1000).toFixed(0)}s`);
        logoutTimeout = setTimeout(() => {
            refreshFn().catch((err) => {
                log.error('Error al refrescar token automáticamente:', err);
            });
        }, delay);
    }
    catch (error) {
        log.error('setupTokenAutoRefresh: error al calcular el tiempo de refresco', error);
    }
});
export const useAuthStore = create()(persist((set, get) => {
    const setAuthState = (auth) => {
        set((state) => (Object.assign({ token: null, refreshToken: null, clientToken: null, expirationDate: null, companyId: null, isAuthenticated: false, isAuthReady: false, logoutReason: state.logoutReason }, auth)));
    };
    return {
        token: null,
        refreshToken: null,
        expirationDate: null,
        companyId: null,
        isAuthenticated: false,
        clientToken: null,
        isAuthReady: false,
        logoutReason: null,
        login: (email, password) => __awaiter(void 0, void 0, void 0, function* () {
            yield get().setClientToken();
            const { expiration, accessToken, refreshToken } = yield loginUseCase(email, password);
            const companyId = Number(getCompanyIdFromToken(accessToken));
            axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            const company = yield getCompanyInfoV2(companyId);
            useCompanyStore.getState().setCompany(company);
            setAuthState({
                token: accessToken,
                refreshToken,
                expirationDate: expiration,
                companyId,
                isAuthenticated: true,
                isAuthReady: true,
                logoutReason: null,
            });
            setupTokenAutoRefresh(accessToken, get().refreshSession);
        }),
        refreshSession: () => __awaiter(void 0, void 0, void 0, function* () {
            const currentRefreshToken = get().refreshToken;
            if (!currentRefreshToken) {
                yield get().logout(true);
                return;
            }
            const response = yield refreshTokenUseCase(currentRefreshToken);
            // Verificación segura del resultado
            if (!response || !response.accessToken || !response.expiration || !response.refreshToken) {
                log.warn('Refresh token inválido o expirado');
                yield get().logout(true);
                return;
            }
            const { accessToken, expiration, refreshToken: newRefreshToken } = response;
            const companyId = Number(getCompanyIdFromToken(accessToken));
            axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            const company = yield getCompanyInfoV2(companyId);
            useCompanyStore.getState().setCompany(company);
            setAuthState({
                token: accessToken,
                refreshToken: newRefreshToken,
                expirationDate: expiration,
                companyId,
                isAuthenticated: true,
                isAuthReady: true,
                logoutReason: null,
            });
            setupTokenAutoRefresh(accessToken, get().refreshSession);
        }),
        loginFromSage: (encryptedId) => __awaiter(void 0, void 0, void 0, function* () {
            yield get().setClientToken();
            const { expiration, accessToken, refreshToken: newRefreshToken } = yield sageLogin(encryptedId);
            const companyId = Number(getCompanyIdFromToken(accessToken));
            axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            const company = yield getCompanyInfoV2(companyId);
            useCompanyStore.getState().setCompany(company);
            setAuthState({
                token: accessToken,
                expirationDate: expiration,
                refreshToken: newRefreshToken,
                companyId,
                isAuthenticated: true,
                isAuthReady: true,
                logoutReason: null,
            });
            setupTokenAutoRefresh(accessToken, get().refreshSession);
        }),
        logout: (isTokenExpired = false) => __awaiter(void 0, void 0, void 0, function* () {
            delete axios.defaults.headers.common['Authorization'];
            if (isTokenExpired) {
                get().setLogoutReason(i18n.t('common.message.tokenExpired'));
            }
            yield get().setClientToken();
        }),
        setLogoutReason: (reason) => set({ logoutReason: reason }),
        getCompanyId: () => {
            const token = get().token;
            return token ? Number(getCompanyIdFromToken(token)) : null;
        },
        setClientToken: () => __awaiter(void 0, void 0, void 0, function* () {
            useCompanyStore.getState().clearCompany();
            const { expiration, accessToken } = yield getClientTokenUseCase();
            axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
            setAuthState({
                clientToken: accessToken,
                expirationDate: expiration,
                isAuthenticated: false,
                token: null,
                companyId: null,
                isAuthReady: true,
            });
        }),
        removeClientToken: () => {
            delete axios.defaults.headers.common['Authorization'];
            setAuthState({ clientToken: null });
        },
        initAuth: () => __awaiter(void 0, void 0, void 0, function* () {
            const { token, clientToken, expirationDate, refreshToken } = get();
            if (isTokenValid(token)) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                const companyId = Number(getCompanyIdFromToken(token));
                const company = yield getCompanyInfoV2(companyId);
                useCompanyStore.getState().setCompany(company);
                setAuthState({
                    token,
                    companyId,
                    expirationDate,
                    refreshToken,
                    isAuthenticated: true,
                    isAuthReady: true,
                });
                setupTokenAutoRefresh(token, get().refreshSession);
            }
            else if (isTokenValid(clientToken)) {
                useCompanyStore.getState().clearCompany();
                axios.defaults.headers.common['Authorization'] = `Bearer ${clientToken}`;
                setAuthState({
                    clientToken,
                    isAuthenticated: false,
                    expirationDate,
                    isAuthReady: true,
                });
            }
            else {
                yield get().logout();
            }
        }),
    };
}, {
    name: 'auth-storage',
    partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        clientToken: state.clientToken,
        expirationDate: state.expirationDate,
        companyId: state.companyId,
        isAuthenticated: state.isAuthenticated,
        isAuthReady: state.isAuthReady,
    }),
    onRehydrateStorage: () => {
        return (state) => __awaiter(void 0, void 0, void 0, function* () {
            const token = (state === null || state === void 0 ? void 0 : state.token) || (state === null || state === void 0 ? void 0 : state.clientToken);
            if (token) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            }
            setTimeout(() => {
                useAuthStore
                    .getState()
                    .initAuth()
                    .catch((e) => console.error('initAuth failed:', e));
            }, 0);
        });
    },
}));
